import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireAdmin } from "./supabase.server";

const inviteSchema = z.object({
  accessToken: z.string().min(20),
  eventId: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string().trim().min(2).max(100),
  role: z.enum(["partner", "client"]),
  redirectTo: z.string().url(),
});

export const inviteEventMember = createServerFn({ method: "POST" })
  .validator((input: unknown) => inviteSchema.parse(input))
  .handler(async ({ data }) => {
    const { admin } = await requireAdmin(data.accessToken);
    const email = data.email.toLowerCase();
    const { data: currentMember } = await admin
      .from("event_members")
      .select("user_id")
      .eq("event_id", data.eventId)
      .eq("invited_email", email)
      .maybeSingle();
    if (currentMember) return { userId: currentMember.user_id as string };

    const { data: usersPage, error: usersError } = await admin.auth.admin.listUsers({
      page: 1,
      perPage: 1000,
    });
    if (usersError) throw new Error(usersError.message);
    let invitedUser = usersPage.users.find((user) => user.email?.toLowerCase() === email);
    let createdUser = false;
    if (!invitedUser) {
      const { data: invited, error: inviteError } = await admin.auth.admin.inviteUserByEmail(
        email,
        {
          redirectTo: data.redirectTo,
          data: { display_name: data.displayName },
        },
      );
      if (inviteError || !invited.user)
        throw new Error(inviteError?.message ?? "No se pudo invitar");
      invitedUser = invited.user;
      createdUser = true;
    }

    const { error } = await admin.from("event_members").upsert({
      event_id: data.eventId,
      user_id: invitedUser.id,
      role: data.role,
      invited_email: email,
    });
    if (error) {
      if (createdUser) await admin.auth.admin.deleteUser(invitedUser.id);
      throw new Error(error.message);
    }
    return { userId: invitedUser.id };
  });
